/**
 * WishlistProvider
 *
 * Usage - Default:
 * <WishlistProvider disableMeta />
 *
 * Usage - Mini wishlist (meta disabled by default):
 * <WishlistProvider miniWishlist />
 *
 * Usage - overriding individual components, while maintaining logic and structure:
 * <WishlistProvider
 *  disableMeta
 *  StyledWishlistContainer={CustomComponentWishlistContainer}
 *  WishlistIcon={CustomComponentWishlistIcon}
 *  ...
 * />
 *
 * Usage - Overriding all logic and internal components:
 * <WishlistProvider disableMeta>
 *   <CustomMiniWishlist />
 *   ...
 * </WishlistProvider>
 *
 *
 * disableMeta prop can be used to disable Helmet from applying meta tags. This
 * is useful if this component is included on a page (or in the header) and not
 * in its own page container.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import PropIsValidReactElement from '_platform/src/utils/PropIsValidReactElement';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Route, withRouter } from 'react-router-dom';

// Style and SEO
import Helmet from 'react-helmet';
import { withSettings } from '../../containers/WebApp/SettingsContext';

// import injectReducer from '_platform/src/utils/injectReducer';
// import injectSaga from '_platform/src/utils/injectSaga';
import { wishlistAddRemoveRequest } from '../../containers/WebApp/actions';
// import reducer from './reducer';
// import saga from './saga';
import { selectWishlist } from '../WebApp/selectors';

import { untrailingSlashIt } from '_platform/src/utils/utilities';
import LoadAsync from '_platform/src/utils/LoadAsync';
const WishlistDefault = LoadAsync(() =>
  import(
    /* webpackChunkName: "wishlist" */ '../../components/Wishlist/Wishlist'
  )
);

class WishlistProvider extends Component {
  Meta = () =>
    !this.props.disableMeta &&
    !this.props.isMiniWishlist && (
      <Helmet>
        <title>Wishlist</title>
        {/*<meta name="description" content="Page description" />*/}
      </Helmet>
    );

  render() {
    if (
      this.props.settings.settingsApp.wishlistDisabled ||
      (this.props.settings.program &&
        !this.props.settings.program.enableWishlist)
    ) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(`Dev: Wishlist is disabled in program settings/config`);
      }

      // Hide the wishlist button if the wishlist is disabled
      return null;
    }

    const { Wishlist = WishlistDefault } = this.props;

    const parentPagePath = untrailingSlashIt(this.props.match.url);

    return (
      <React.Fragment>
        <this.Meta />
        <Route
          path={`${parentPagePath}/:step?`}
          render={() => {
            return this.props.children ? (
              React.Children.map(this.props.children, childElement =>
                React.cloneElement(childElement, {
                  ...this.props,
                })
              )
            ) : (
              <Wishlist
                isMiniWishlist={this.props.isMiniWishlist}
                onWishlistAddRemoveRequest={
                  this.props.onWishlistAddRemoveRequest
                }
                settings={this.props.settings}
                StyledMiniWishlistIcon={this.props.StyledMiniWishlistIcon}
                StyledWishlistContainer={this.props.StyledWishlistContainer}
                StyledWishlistContent={this.props.StyledWishlistContent}
                StyledWishlistList={this.props.StyledWishlistList}
                wishlist={this.props.wishlist}
                WishlistIcon={this.props.WishlistIcon}
                WishlistItem={this.props.WishlistItem}
              />
            );
          }}
        />
      </React.Fragment>
    );
  }
}

WishlistProvider.propTypes = {
  children: PropTypes.element,
  disableMeta: PropTypes.bool,
  isMiniWishlist: PropTypes.bool,
  location: PropTypes.object,
  match: PropTypes.object,
  onWishlistAddRemoveRequest: PropTypes.func.isRequired,
  settings: PropTypes.object.isRequired,
  StyledMiniWishlistIcon: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  StyledWishlistContainer: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  StyledWishlistContent: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  StyledWishlistList: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  Wishlist: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  wishlist: PropTypes.array,
  WishlistIcon: PropIsValidReactElement, //eslint-disable-line react/require-default-props
  WishlistItem: PropIsValidReactElement, //eslint-disable-line react/require-default-props
};

WishlistProvider.defaultProps = {
  children: undefined,
  disableMeta: false,
  location: {},
  match: {},
  isMiniWishlist: false,
  wishlist: undefined,
};

const mapStateToProps = createStructuredSelector({
  wishlist: selectWishlist(),
});

function mapDispatchToProps(dispatch, props) {
  return {
    dispatch,
    onWishlistAddRemoveRequest: (action, values) =>
      dispatch(wishlistAddRemoveRequest(action, values)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
// const withReducer = injectReducer({ key: 'wishlist', reducer });
// const withSaga = injectSaga({ key: 'wishlist', saga }); // Prevent saga cancel on component unmount

export default compose(
  withRouter,
  withSettings,
  // withReducer,
  // withSaga,
  withConnect
)(WishlistProvider);
